import { Box, Grid } from '@mui/material';
import { UsersListType } from '@okletsplay/core';
import { useFetchData } from 'app/hooks/store';
import { UserEntity } from 'app/interfaces/entity';
import { UserHighlighterCard } from 'modules/users/components/UserHighlighterCard';
import { DEFAULT_PAGE_SIZE } from 'modules/users/constants';
import { getUsersList } from 'modules/users/store/users/repository';
import { usersSelector, usersSelectorFactory } from 'modules/users/store/users/selectors';
import { usersReducers } from 'modules/users/store/users/slice';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'shared/components/Loader';
import { useStyles } from './PlayersList.styles';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { EmptyPlaceholder } from 'shared/components/EmptyPlaceholder';

interface PlayersListProps {
  search: string;
  type: UsersListType;
  onLoadingStart?: () => void;
  onLoadingEnd?: () => void;
}

export const PlayersList: FC<PlayersListProps> = ({
  search,
  type,
  onLoadingStart,
  onLoadingEnd,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const users: UserEntity[] = useSelector(usersSelector.all);
  const total = useSelector(usersSelectorFactory.useTotal(type));

  const actions = useMemo(
    () => [
      getUsersList({
        type,
        search,
      }),
    ],
    [type, search]
  );
  const { fetch, loading } = useFetchData(actions, { fetchOnInit: false });

  const loadMoreHandler = useCallback(() => {
    dispatch(usersReducers.updatePagination({ identifier: type, data: { reload: false } }));
    fetch();
  }, [dispatch, fetch, type]);

  useEffect(() => {
    if (search.length) {
      dispatch(
        usersReducers.updatePagination({
          identifier: type,
          data: { limit: DEFAULT_PAGE_SIZE, offset: 0, reload: true },
        })
      );
    } else {
      dispatch(
        usersReducers.updatePagination({
          identifier: type,
          data: { limit: DEFAULT_PAGE_SIZE, offset: 0, reload: true },
        })
      );
    }
    onLoadingStart?.();
    fetch().finally(() => onLoadingEnd?.());
  }, [dispatch, fetch, onLoadingEnd, onLoadingStart, search, type]);

  if (!Boolean(search) && loading) {
    return <Loader />;
  }

  if (!users?.length && !loading) {
    return (
      <Box className={classes.emptyPlaceholder}>
        <EmptyPlaceholder text="Get out there and play some games!" />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container className={classes.list}>
        {users.map((user) => (
          <Grid item key={user.id}>
            <UserHighlighterCard user={user} searchText={search} />
          </Grid>
        ))}
      </Grid>
      {total !== users.length && (
        <LoaderButton
          fullWidth
          variant="outlined"
          color="secondary"
          onClick={loadMoreHandler}
          disabled={loading}
          loading={loading}
        >
          Load More
        </LoaderButton>
      )}
    </Box>
  );
};
